import { FC, useMemo } from 'react';
import styled from 'styled-components';

import { useI18nValues } from '@/entities/vfo/hooks/useI18nValues';
import { Member, Tags, UserStatus } from '@/entities/vfo/types';
import {
  BarChartItem,
  DoughnutChartItem,
  EmptyMessage,
  VfoBarChart,
  VfoDoughnutChart,
  WidgetContainer,
} from '@/features/vfo/components';
import { getInvitationBreakdown } from '@/features/vfo/helpers';
import { ApiDashboardResponseV2 } from '@/shared/api';
import { breakpoints, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals';

type MembersDashboardProps = {
  caseId?: string;
  analyticsData?: ApiDashboardResponseV2;
  members: Member[];
};

export const MembersDashboard: FC<MembersDashboardProps> = ({ caseId, analyticsData, members }) => {
  const { t } = useTranslation('features/vfo', { keyPrefix: 'dashboard' });
  const { t: tc } = useTranslation('features/vfo', {
    keyPrefix: caseId ? 'membersList.caseMembers' : 'membersList.allMembers',
  });
  const { tag: tagTranslation } = useI18nValues();

  const modals = useModalsStore();

  const memberKpi = analyticsData?.allMembers.analytics.kpi;

  const invitationStatusItems = useMemo<BarChartItem[]>(() => {
    const invitationBreakdown = getInvitationBreakdown(analyticsData);

    return [
      {
        label: t('invited'),
        value: invitationBreakdown.invited,
        color: COLORS.colorVfoTags[0],
      },
      {
        label: t('registered'),
        value: invitationBreakdown.registered,
        color: COLORS.colorVfoTags[2],
      },
    ];
  }, [analyticsData]);

  const tagDistributionItems = useMemo<DoughnutChartItem[]>(() => {
    const order = [
      Tags.FAMILY,
      Tags.LEGAL,
      Tags.WEALTH,
      Tags.TAX,
      Tags.FINANCE,
      Tags.MANAGEMENT,
      Tags.LIFESTYLE,
    ];

    const tags = analyticsData?.allMembers.analytics?.charts.tagDistribution;

    return order
      .map((tag) => ({
        id: tag,
        label: tagTranslation(tag),
        value: tags?.[tag] ?? 0,
        color: COLORS.colorVfoTags[order.indexOf(tag) % COLORS.colorVfoTags.length],
      }))
      .filter((item) => item.value > 0);
  }, [analyticsData]);

  const activeMemberCount = useMemo(() => {
    return members.reduce(
      (acc, member) => (member.status === UserStatus.ONLINE ? acc + 1 : acc),
      0,
    );
  }, [members]);

  if (!members.length) {
    return (
      <EmptyMessage
        iconName="users-three"
        text={tc('noMembers')}
        buttonText={tc('addNewBtn')}
        onClick={() => {
          if (caseId) {
            modals.openModal({
              type: 'AddMembersToCaseModal',
              caseId,
            });
          } else {
            modals.openModal({
              type: 'InviteMemberModal',
            });
          }
        }}
      />
    );
  }

  return (
    <Container>
      <UsersStatisticsWrapper>
        <WidgetContainerStyled
          size="small"
          icon="users-four"
          title={`${memberKpi?.totalMembers ?? 0} ${t('totalMembers')}`}
          titleClassName="titleWrapper"
        />
        <WidgetContainerStyled
          size="small"
          icon="user-circle-check"
          title={`${activeMemberCount} ${t('activeNow')}`}
          titleClassName="titleWrapper"
        />
        <WidgetContainerStyled
          size="small"
          icon="user-circle-dashed"
          title={`${memberKpi?.inactive7Days ?? 0} ${t('inactive')}`}
          subTitle={t('7Days')}
          titleClassName="titleWrapper"
        />
      </UsersStatisticsWrapper>
      {!caseId && (
        <VfoBarChartStyled
          title={t('invitationStatusBreakdownTitle')}
          items={invitationStatusItems}
        />
      )}
      <Separator />
      <VfoDoughnutChart title={t('tagDistributionTitle')} items={tagDistributionItems} />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  gap: 24px;
  border-radius: 16px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column;
    gap: 0;
    padding: 6px;
    background: ${COLORS.white};
  }
`;

const UsersStatisticsWrapper = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;
  justify-content: space-between;
  flex: 3;
  padding: 12px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: row;
    gap: 8px;
    border-bottom: 1px solid ${COLORS.systemDark50A};
  }
`;

const WidgetContainerStyled = styled(WidgetContainer)`
  && {
    ${media.md}, ${breakpoints.sm} {
      padding: 0;
      flex: 1;

      .titleWrapper {
        flex-direction: column;
        gap: 6px;
        text-align: center;
        font-size: 12px;
      }

      &:nth-child(2) {
        order: 1;
      }
    }
  }
`;

const Separator = styled.div`
  height: 1px;
  background: ${COLORS.systemDark50A};
`;

const VfoBarChartStyled = styled(VfoBarChart)`
  flex: 3;
  width: 33%;
  ${media.md}, ${breakpoints.sm} {
    border-radius: 0;
    padding: 12px;
    width: 100%;
  }
`;
